/* סרגל הסינון של מסך הפעולות: חיפוש, טווח (התקופה או הכל), סוג, מקור, קטגוריות, מיון.
   כל כפתור מראה מה הוא עושה, ולחיצה נוספת מבטלת אותו. */
import { useRef } from 'react';
import { cats } from '../../domain/categories.js';
import { applyTxFilter, catKey, txfCount, txfDefault } from '../../domain/txFilter.js';
import { periodShort } from '../../domain/periods.js';
import { Sheet } from '../../components/ui.jsx';
import { Icon } from '../../components/Icon.jsx';
import { openModal, closeModal } from '../../app/nav.js';
import { UI, setTxf, useApp } from '../../store/ui.js';

const Chip = ({ on, onClick, children, title }) => (
  <button type="button" className={'fchip' + (on ? ' on' : '')} aria-pressed={on} onClick={onClick} title={title}>{children}</button>
);

export function TxFilters({ base }) {
  const f = UI.txf;
  const inp = useRef(null);
  const toggle = (k, v) => setTxf({ [k]: f[k] === v ? 'all' : v });
  const selCats = f.cats.map((k) => { const [t, id] = k.split(':'); return cats(t).find((c) => c.id === id); }).filter(Boolean);
  const catLbl = !selCats.length ? 'קטגוריה' : selCats.length === 1 ? selCats[0].icon + ' ' + selCats[0].label : selCats[0].icon + ' ' + selCats[0].label + ' +' + (selCats.length - 1);
  return (
    <div className="txf">
      <div className="txf-top">
        <label className="search-i">
          <Icon name="search" size={17} />
          <input ref={inp} value={f.q} onChange={(e) => setTxf({ q: e.target.value })} onKeyDown={(e) => e.key === 'Escape' && setTxf({ q: '' })}
            placeholder={f.all ? 'חיפוש בכל התקופות: שם עסק או סכום' : 'חיפוש: שם עסק או סכום'} aria-label="חיפוש" />
          {f.q && <button type="button" className="search-x" onClick={() => { setTxf({ q: '' }); inp.current && inp.current.focus(); }} aria-label="נקה חיפוש"><Icon name="x" size={15} /></button>}
        </label>
      </div>
      <div className="txf-chips">
        <Chip on={f.all} onClick={() => setTxf({ all: !f.all })} title={f.all ? 'מציג את כל התקופות' : 'מציג רק את ' + periodShort(UI.viewKey)}>📅 כל התקופות</Chip>
        <span className="txf-sep" />
        <Chip on={f.type === 'expense'} onClick={() => toggle('type', 'expense')}>הוצאות</Chip>
        <Chip on={f.type === 'income'} onClick={() => toggle('type', 'income')}>הכנסות</Chip>
        <span className="txf-sep" />
        <Chip on={f.src === 'cash'} onClick={() => toggle('src', 'cash')}>🏦 עו"ש</Chip>
        <Chip on={f.src === 'credit'} onClick={() => toggle('src', 'credit')}>💳 אשראי</Chip>
        <span className="txf-sep" />
        <Chip on={selCats.length > 0} onClick={() => openModal(() => <CatPicker base={base} />)}>{catLbl} <Icon name="chevD" size={14} /></Chip>
        <Chip on={f.uncat} onClick={() => setTxf({ uncat: !f.uncat, cats: [] })} title='הוצאות שבקטגוריה "אחר"'>📦 לא מסווג</Chip>
        <Chip on={f.hideFixed} onClick={() => setTxf({ hideFixed: !f.hideFixed })} title="מסתיר שכר דירה, משכורת, מנויים וכו'">🔁 בלי חיובים קבועים</Chip>
        <span className="txf-sep" />
        <Chip on={f.sort === 'amount'} onClick={() => setTxf({ sort: f.sort === 'amount' ? 'date' : 'amount' })} title="מיון">
          {f.sort === 'amount' ? '↓ הכי גדולות קודם' : '↓ הכי חדשות קודם'}
        </Chip>
      </div>
    </div>
  );
}

/** שורת "מה מסונן": כל מסנן פעיל כתגית עם ×, מספר התוצאות, ו"נקה הכל" */
export function TxfResult({ txs, hidden }) {
  const f = UI.txf;
  if (!txfCount(f) && !hidden.n) return null;
  const tags = [];
  if (f.q.trim()) tags.push(['🔍 "' + f.q.trim() + '"', { q: '' }]);
  if (f.all) tags.push(['📅 כל התקופות', { all: false }]);
  if (f.type !== 'all') tags.push([f.type === 'income' ? 'הכנסות' : 'הוצאות', { type: 'all' }]);
  if (f.src !== 'all') tags.push([f.src === 'credit' ? '💳 אשראי' : '🏦 עו"ש', { src: 'all' }]);
  f.cats.forEach((k) => {
    const [t, id] = k.split(':'), c = cats(t).find((x) => x.id === id);
    if (c) tags.push([c.icon + ' ' + c.label, { cats: f.cats.filter((x) => x !== k) }]);
  });
  if (f.uncat) tags.push(['📦 לא מסווג', { uncat: false }]);
  if (f.hideFixed) tags.push(['🔁 בלי חיובים קבועים' + (hidden.n ? ' (' + hidden.n + ')' : ''), { hideFixed: false }]);
  return (
    <div className="txf-res">
      <div className="txf-tags">
        <b>{txs.length === 1 ? 'פעולה אחת' : txs.length + ' פעולות'}</b>
        {tags.map(([label, patch], i) => (
          <button type="button" key={i} className="txf-tag" onClick={() => setTxf(patch)} aria-label={'הסר ' + label}>{label}<Icon name="x" size={13} /></button>
        ))}
      </div>
      {tags.length > 1 && <button type="button" className="link nowrap" onClick={clearTxf}>נקה הכל</button>}
    </div>
  );
}

export function clearTxf() {
  setTxf({ ...txfDefault(), sort: UI.txf.sort });
}

/** בחירת קטגוריות: כמה ביחד, עם מספר הפעולות בכל אחת. השינוי חל מיד */
function CatPicker({ base }) {
  useApp();
  const f = UI.txf;
  const scoped = applyTxFilter(base, f, ['cats']).txs;
  const count = {};
  scoped.forEach((t) => { const k = catKey(t); count[k] = (count[k] || 0) + 1; });
  const sel = new Set(f.cats);
  const flip = (k) => { const s = new Set(sel); if (s.has(k)) s.delete(k); else s.add(k); setTxf({ cats: [...s], uncat: false }); };
  const types = f.type === 'income' ? ['income'] : f.type === 'expense' ? ['expense'] : ['expense', 'income'];
  const total = applyTxFilter(base, f).txs.length;
  return (
    <Sheet title="בחירת קטגוריות">
      <div className="note mb12">אפשר לבחור כמה. המספר הוא כמה פעולות יש בכל קטגוריה לפי שאר הסינון.</div>
      {types.map((ty) => {
        const list = cats(ty).map((c) => ({ c, k: ty + ':' + c.id, n: count[ty + ':' + c.id] || 0 }))
          .sort((a, b) => (b.n > 0) - (a.n > 0) || b.n - a.n);
        return (
          <div key={ty} className="mb12">
            {types.length > 1 && <div className="rv-h" style={{ marginTop: 0 }}>{ty === 'income' ? 'הכנסות' : 'הוצאות'}</div>}
            <div className="cat-grid">
              {list.map(({ c, k, n }) => (
                <button type="button" key={k} className={'cat-tile' + (sel.has(k) ? ' on' : '') + (n ? '' : ' empty')} aria-pressed={sel.has(k)} onClick={() => flip(k)}>
                  <span className="cat-tile-ico">{c.icon}</span><span className="cat-tile-lbl">{c.label}</span><span className="cat-tile-n">{n}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div className="row sheet-actions">
        <button type="button" className="ubtn ubtn-primary" style={{ flex: 2 }} onClick={closeModal}>הצג {total === 1 ? 'פעולה אחת' : total + ' פעולות'}</button>
        {sel.size > 0 && <button type="button" className="ubtn ubtn-ghost" style={{ flex: 1 }} onClick={() => setTxf({ cats: [] })}>נקה</button>}
      </div>
    </Sheet>
  );
}
